/**
 * A tiny observable registry keyed by id. Commands, shortcuts and search
 * providers each live in one; React reads them with useSyncExternalStore.
 * Registering an existing id replaces it (last registration wins) and the
 * returned function removes exactly that registration.
 */
export interface Registry<T extends { id: string }> {
  register(entry: T): () => void;
  get(): readonly T[];
  find(id: string): T | undefined;
  subscribe(listener: () => void): () => void;
  clear(): void;
}

export function createRegistry<T extends { id: string }>(): Registry<T> {
  // Stack per id so an unregister restores the previous owner (StrictMode,
  // nested scopes) instead of deleting the id outright.
  const stacks = new Map<string, Array<{ token: symbol; entry: T }>>();
  let snapshot: readonly T[] = [];
  const listeners = new Set<() => void>();

  const rebuild = () => {
    const out: T[] = [];
    for (const stack of stacks.values()) {
      const top = stack[stack.length - 1];
      if (top) out.push(top.entry);
    }
    snapshot = out;
    for (const l of listeners) l();
  };

  return {
    register(entry) {
      const token = Symbol(entry.id);
      const stack = stacks.get(entry.id) ?? [];
      stack.push({ token, entry });
      stacks.set(entry.id, stack);
      rebuild();
      return () => {
        const s = stacks.get(entry.id);
        if (!s) return;
        const i = s.findIndex((x) => x.token === token);
        if (i < 0) return;
        s.splice(i, 1);
        if (s.length === 0) stacks.delete(entry.id);
        rebuild();
      };
    },
    get: () => snapshot,
    find: (id) => {
      const s = stacks.get(id);
      return s?.[s.length - 1]?.entry;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    clear() {
      stacks.clear();
      rebuild();
    },
  };
}
